<template>
  <AdminLayout page-title="Safety & Compliance" page-subtitle="Monitor safety metrics, report incidents, and maintain compliance standards">
    <!-- Tab Navigation -->
    <div class="mb-8">
      <nav class="flex space-x-1 bg-slate-800/40 backdrop-blur-sm rounded-2xl p-2 border border-slate-700/50">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="flex-1 px-6 py-3 text-sm font-medium rounded-xl transition-all duration-200 group"
          :class="activeTab === tab.id 
            ? 'bg-gradient-to-r from-emerald-400 to-cyan-400 text-slate-900 shadow-lg shadow-emerald-500/25' 
            : 'text-slate-400 hover:text-emerald-400 hover:bg-slate-700/50'"
        >
          <div class="flex items-center justify-center space-x-2">
            <svg v-if="tab.id === 'safety-first'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
            <svg v-else-if="tab.id === 'certifications'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
            </svg>
            <svg v-else-if="tab.id === 'incidents'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
            </svg>
            <span>{{ tab.label }}</span>
          </div>
        </button>
      </nav>
    </div>

    <!-- Safety First Tab -->
    <div class="space-y-8" v-if="activeTab === 'safety-first'">
      <!-- Safety Overview Dashboard -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="safety-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Safety Score</p>
              <p class="text-3xl font-bold text-white">{{ safetyStats.safetyScore }}/100</p>
              <p class="text-sm text-emerald-400 mt-2">+{{ safetyStats.improvement }} this month</p>
            </div>
            <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-2xl flex items-center justify-center text-blue-400">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-3a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="stat-content">
            <p class="stat-label">Days Since Incident</p>
            <p class="stat-value">{{ safetyStats.daysSinceIncident }}</p>
            <p class="stat-change text-emerald-400">Record: {{ safetyStats.recordDays }}</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-2xl flex items-center justify-center text-emerald-400">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="stat-content">
            <p class="stat-label">Active Crew</p>
            <p class="stat-value">{{ safetyStats.activeCrew }}</p>
            <p class="stat-change text-emerald-400">{{ safetyStats.compliantCrew }} compliant</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-2xl flex items-center justify-center text-yellow-400">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="stat-content">
            <p class="stat-label">Certifications</p>
            <p class="stat-value">{{ safetyStats.totalCertifications }}</p>
            <p class="stat-change text-yellow-400">{{ safetyStats.expiringSoon }} expiring</p>
          </div>
        </div>
      </div>

      <!-- Incident Reporting & Near-Miss Logging -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Incident Reporting -->
        <div class="card">
          <div class="card-header">
            <h3 class="text-lg font-semibold text-white">Incident Reporting</h3>
            <button @click="showIncidentModal = true" class="btn-primary btn-sm">
              <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
              </svg>
              Report Incident
            </button>
          </div>
          <div class="card-body">
            <div v-if="incidents.length === 0" class="text-center py-8">
              <svg class="w-16 h-16 text-emerald-400 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <h3 class="text-lg font-medium text-emerald-400 mb-2">No incidents reported</h3>
              <p class="text-emerald-300">Great job maintaining safety standards!</p>
            </div>

            <div v-else class="space-y-4">
              <div v-for="incident in incidents" :key="incident.id" class="border border-slate-600/50 rounded-lg p-4">
                <div class="flex items-start justify-between mb-3">
                  <div>
                    <h4 class="font-medium text-white">{{ incident.title }}</h4>
                    <p class="text-sm text-slate-300">{{ incident.description }}</p>
                    <p class="text-xs text-slate-400 mt-1">Reported by {{ incident.reportedBy }} on {{ formatDate(incident.reportedAt) }}</p>
                  </div>
                  <span :class="getIncidentSeverityBadgeClass(incident.severity)" class="badge">
                    {{ incident.severity }}
                  </span>
                </div>
                
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="font-medium text-slate-200">Location:</span>
                    <span class="ml-2 text-slate-300">{{ incident.location }}</span>
                  </div>
                  <div>
                    <span class="font-medium text-slate-200">Status:</span>
                    <span class="ml-2 text-slate-300">{{ incident.status }}</span>
                  </div>
                </div>
                
                <div class="mt-3 flex items-center space-x-2">
                  <button @click="viewIncident(incident)" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
                    View Details
                  </button>
                  <button @click="updateIncidentStatus(incident)" class="text-slate-300 hover:text-slate-200 text-sm font-medium">
                    Update Status
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Near-Miss Logging -->
        <div class="card">
          <div class="card-header">
            <h3 class="text-lg font-semibold text-white">Near-Miss Logging</h3>
            <button @click="showNearMissModal = true" class="btn-warning btn-sm">
              <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
              </svg>
              Log Near-Miss
            </button>
          </div>
          <div class="card-body">
            <div v-if="nearMisses.length === 0" class="text-center py-8">
              <svg class="w-16 h-16 text-emerald-400 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <h3 class="text-lg font-medium text-emerald-400 mb-2">No near-misses logged</h3>
              <p class="text-emerald-300">Excellent safety awareness!</p>
            </div>

            <div v-else class="space-y-4">
              <div v-for="nearMiss in nearMisses" :key="nearMiss.id" class="border border-yellow-500/30 bg-slate-700/20 rounded-lg p-4">
                <div class="flex items-start justify-between mb-3">
                  <div>
                    <h4 class="font-medium text-yellow-400">{{ nearMiss.title }}</h4>
                    <p class="text-sm text-slate-300">{{ nearMiss.description }}</p>
                    <p class="text-xs text-yellow-400 mt-1">Logged by {{ nearMiss.loggedBy }} on {{ formatDate(nearMiss.loggedAt) }}</p>
                  </div>
                  <span :class="getNearMissTypeBadgeClass(nearMiss.type)" class="badge">
                    {{ nearMiss.type }}
                  </span>
                </div>
                
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="font-medium text-amber-400">Location:</span>
                    <span class="ml-2 text-yellow-400">{{ nearMiss.location }}</span>
                  </div>
                  <div>
                    <span class="font-medium text-amber-400">Action Taken:</span>
                    <span class="ml-2 text-yellow-400">{{ nearMiss.actionTaken }}</span>
                  </div>
                </div>
                
                <div class="mt-3 flex items-center space-x-2">
                  <button @click="viewNearMiss(nearMiss)" class="text-yellow-400 hover:text-yellow-300 text-sm font-medium">
                    View Details
                  </button>
                  <button @click="markResolved(nearMiss)" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
                    Mark Resolved
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Certifications Tab -->
    <div class="space-y-8" v-if="activeTab === 'certifications'">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Certification Overview -->
        <div class="card">
          <div class="card-header">
            <h3 class="text-lg font-semibold text-white">Certification Overview</h3>
            <button @click="showAddCertificationModal = true" class="btn-oilbuddy-primary btn-sm">
              <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
              </svg>
              Add Certification
            </button>
          </div>
          <div class="card-body">
            <div class="grid grid-cols-2 gap-4 mb-6">
              <div class="stat-card">
                <div class="stat-icon bg-gradient-to-br from-emerald-500/20 to-cyan-500/20">
                  <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                  </svg>
                </div>
                <div>
                  <p class="stat-label">Active Certifications</p>
                  <p class="stat-value">{{ certificationStats.active }}</p>
                </div>
              </div>
              <div class="stat-card">
                <div class="stat-icon bg-gradient-to-br from-amber-500/20 to-orange-500/20">
                  <svg class="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div>
                  <p class="stat-label">Expiring Soon</p>
                  <p class="stat-value">{{ certificationStats.expiring }}</p>
                </div>
              </div>
            </div>

            <!-- Certifications List -->
            <div v-if="certifications.length === 0" class="text-center py-8">
              <svg class="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
              </svg>
              <h3 class="text-lg font-medium text-slate-300 mb-2">No certifications yet</h3>
              <p class="text-slate-400">Add certifications to track expiry dates and renewals.</p>
            </div>

            <div v-else class="space-y-4">
              <div v-for="cert in certifications" :key="cert.id" class="border border-slate-600/50 rounded-lg p-4">
                <div class="flex items-start justify-between mb-3">
                  <div>
                    <h4 class="font-medium text-white">{{ cert.name }}</h4>
                    <p class="text-sm text-slate-300">{{ cert.provider }}</p>
                    <p class="text-xs text-slate-400 mt-1">Expires: {{ formatDate(cert.expiryDate) }}</p>
                  </div>
                  <span :class="getCertificationStatusClass(cert.status)" class="badge">
                    {{ cert.status }}
                  </span>
                </div>
                <div class="flex items-center space-x-2">
                  <button @click="renewCertification(cert)" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
                    Renew
                  </button>
                  <button @click="viewCertification(cert)" class="text-slate-300 hover:text-slate-200 text-sm font-medium">
                    View Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Certification Calendar -->
        <div class="card">
          <div class="card-header">
            <h3 class="text-lg font-semibold text-white">Upcoming Renewals</h3>
          </div>
          <div class="card-body">
            <div v-if="upcomingRenewals.length === 0" class="text-center py-8">
              <svg class="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <h3 class="text-lg font-medium text-slate-300 mb-2">No upcoming renewals</h3>
              <p class="text-slate-400">All certifications are up to date.</p>
            </div>

            <div v-else class="space-y-3">
              <div v-for="renewal in upcomingRenewals" :key="renewal.id" class="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div>
                  <p class="font-medium text-white">{{ renewal.certification }}</p>
                  <p class="text-sm text-slate-400">{{ renewal.holder }}</p>
                </div>
                <div class="text-right">
                  <p class="text-sm font-medium" :class="renewal.urgent ? 'text-red-400' : 'text-amber-400'">
                    {{ renewal.daysLeft }} days
                  </p>
                  <p class="text-xs text-slate-400">{{ formatDate(renewal.expiryDate) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Safety Incidents Tab -->
    <div class="space-y-8" v-if="activeTab === 'training'">
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold text-white">Training</h3>
        </div>
        <div class="card-body">
          <p class="text-slate-400">Platform training records stay in the admin panel. No user or demo session is loaded here.</p>
        </div>
      </div>
    </div>

    <div class="space-y-8" v-if="activeTab === 'incidents'">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Incident Statistics -->
        <div class="card">
          <div class="card-header">
            <h3 class="text-lg font-semibold text-white">Incident Statistics</h3>
          </div>
          <div class="card-body">
            <div class="space-y-4">
              <div class="stat-item">
                <div class="stat-icon bg-gradient-to-br from-emerald-500/20 to-cyan-500/20">
                  <svg class="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                  </svg>
                </div>
                <div>
                  <p class="stat-label">Days Since Last Incident</p>
                  <p class="stat-value">{{ safetyStats.daysSinceIncident }}</p>
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-icon bg-gradient-to-br from-blue-500/20 to-indigo-500/20">
                  <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4"/>
                  </svg>
                </div>
                <div>
                  <p class="stat-label">Total Incidents (YTD)</p>
                  <p class="stat-value">{{ incidentStats.total }}</p>
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-icon bg-gradient-to-br from-amber-500/20 to-orange-500/20">
                  <svg class="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                  </svg>
                </div>
                <div>
                  <p class="stat-label">Near Misses (YTD)</p>
                  <p class="stat-value">{{ incidentStats.nearMisses }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Incidents -->
        <div class="lg:col-span-2 card">
          <div class="card-header">
            <h3 class="text-lg font-semibold text-white">Recent Incidents</h3>
            <button @click="showIncidentModal = true" class="btn-oilbuddy-primary btn-sm">
              <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
              </svg>
              Report Incident
            </button>
          </div>
          <div class="card-body">
            <div v-if="allIncidents.length === 0" class="text-center py-8">
              <svg class="w-16 h-16 text-emerald-400 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <h3 class="text-lg font-medium text-emerald-400 mb-2">No incidents reported</h3>
              <p class="text-emerald-300">Excellent safety record!</p>
            </div>

            <div v-else class="space-y-4">
              <div v-for="incident in allIncidents" :key="incident.id" class="border border-slate-600/50 rounded-lg p-4">
                <div class="flex items-start justify-between mb-3">
                  <div>
                    <h4 class="font-medium text-white">{{ incident.title }}</h4>
                    <p class="text-sm text-slate-300">{{ incident.description }}</p>
                    <p class="text-xs text-slate-400 mt-1">
                      Reported by {{ incident.reportedBy }} on {{ formatDate(incident.reportedAt) }}
                    </p>
                  </div>
                  <div class="flex flex-col items-end space-y-2">
                    <span :class="getIncidentSeverityBadgeClass(incident.severity)" class="badge">
                      {{ incident.severity }}
                    </span>
                    <span :class="getIncidentStatusClass(incident.status)" class="badge">
                      {{ incident.status }}
                    </span>
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-4 text-sm mb-3">
                  <div>
                    <span class="font-medium text-slate-200">Location:</span>
                    <span class="ml-2 text-slate-300">{{ incident.location }}</span>
                  </div>
                  <div>
                    <span class="font-medium text-slate-200">Type:</span>
                    <span class="ml-2 text-slate-300">{{ incident.type || 'General' }}</span>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <button @click="viewIncident(incident)" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
                    View Details
                  </button>
                  <button @click="updateIncidentStatus(incident)" class="text-slate-300 hover:text-slate-200 text-sm font-medium">
                    Update Status
                  </button>
                  <button v-if="incident.status !== 'resolved'" @click="resolveIncident(incident)" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
                    Mark Resolved
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { useUserStore } from '@/stores/userStore'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import AdminLayout from '../admin/AdminLayout.vue'

const route = useRoute()
const userStore = useUserStore()
const isAdminRoute = computed(() => route.path.startsWith('/admin'))
const isAdmin = computed(() => isAdminRoute.value || !!localStorage.getItem('adminToken') || userStore.isAdmin)

const activeTab = ref('safety-first')
const showIncidentModal = ref(false)
const showNearMissModal = ref(false)
const showAddCertificationModal = ref(false)
const kpiTimeframe = ref('month')

// User-specific state
const activeUserTab = ref('')
const showUserIncidentModal = ref(false)
const userIncidentForm = reactive({
  type: '',
  description: '',
  location: ''
})

// Tab configuration
const tabs = ref([
  {
    id: 'safety-first',
    label: 'Safety First'
  },
  {
    id: 'certifications',
    label: 'Certifications'
  },
  {
    id: 'training',
    label: 'Training'
  },
  {
    id: 'incidents',
    label: 'Safety Incidents'
  }
])

watch(() => route.path, (path) => {
  if (path.includes('/certifications')) activeTab.value = 'certifications'
  else if (path.includes('/training')) activeTab.value = 'training'
  else if (path.includes('/incidents')) activeTab.value = 'incidents'
}, { immediate: true })

// Forms
const incidentForm = reactive({
  title: '',
  description: '',
  severity: '',
  location: '',
  dateTime: ''
})

const nearMissForm = reactive({
  title: '',
  description: '',
  type: '',
  location: '',
  actionTaken: ''
})

// State
const isLoading = ref(false)
const isSubmitting = ref(false)

// Safety statistics - loaded from API
const safetyStats = ref({
  safetyScore: 0,
  improvement: 0,
  daysSinceIncident: 0,
  recordDays: 0,
  activeCrew: 0,
  compliantCrew: 0,
  totalCertifications: 0,
  expiringSoon: 0
})

const incidents = ref([])
const nearMisses = ref([])
const crewHours = ref([])

const safetyKPIs = ref({
  trir: 0,
  trirTrend: 'down',
  trirChange: 0,
  dart: 0,
  dartTrend: 'down',
  dartChange: 0,
  trainingCompletion: 0,
  trainingTrend: 'up',
  trainingChange: 0
})

// Certification data - zero stats
const certificationStats = ref({
  active: 0,
  expiring: 0
})

const certifications = ref([])
const upcomingRenewals = ref([])

// Incident statistics - zero stats
const incidentStats = ref({
  total: 0,
  nearMisses: 0
})

const allIncidents = ref([])

// User-specific data
const userSafetyStats = ref({
  safetyScore: 95,
  improvement: 3,
  daysSinceIncident: 127,
  recordDays: 365,
  totalCertifications: 4,
  expiringSoon: 1
})

const userCertifications = ref<any[]>([])

// Methods
const getCertificationStatusClass = (status: string) => {
  switch (status) {
    case 'active':
      return 'badge-success'
    case 'expiring':
      return 'badge-warning'
    case 'expired':
      return 'badge-danger'
    default:
      return 'badge-oil'
  }
}

const getIncidentStatusClass = (status: string) => {
  switch (status) {
    case 'resolved':
      return 'badge-success'
    case 'investigating':
      return 'badge-warning'
    case 'open':
      return 'badge-danger'
    default:
      return 'badge-oil'
  }
}

const getIncidentSeverityBadgeClass = (severity: string) => {
  switch (severity) {
    case 'low':
      return 'badge-success'
    case 'medium':
      return 'badge-warning'
    case 'high':
      return 'badge-danger'
    case 'critical':
      return 'badge-danger'
    default:
      return 'badge-oil'
  }
}

const getNearMissTypeBadgeClass = (type: string) => {
  switch (type) {
    case 'equipment':
      return 'badge-primary'
    case 'procedure':
      return 'badge-warning'
    case 'environmental':
      return 'badge-danger'
    case 'behavioral':
      return 'badge-oil'
    default:
      return 'badge-oil'
  }
}

const renewCertification = (cert: any) => {
  console.log('Renewing certification:', cert.id)
  // TODO: Implement certification renewal
}

const viewCertification = (cert: any) => {
  console.log('Viewing certification:', cert.id)
  // TODO: Implement certification viewing
}

const resolveIncident = (incident: any) => {
  console.log('Resolving incident:', incident.id)
  // TODO: Implement incident resolution
  incident.status = 'resolved'
}

const viewIncident = (incident: any) => {
  console.log('Viewing incident:', incident.id)
  // TODO: Implement incident viewing
}

const updateIncidentStatus = (incident: any) => {
  console.log('Updating incident status:', incident.id)
  // TODO: Implement status update
}

const viewNearMiss = (nearMiss: any) => {
  console.log('Viewing near miss:', nearMiss.id)
  // TODO: Implement near miss viewing
}

const markResolved = (nearMiss: any) => {
  console.log('Marking resolved:', nearMiss.id)
  // TODO: Implement resolution
}

const submitIncident = () => {
  console.log('Submitting incident:', incidentForm)
  // TODO: Implement incident submission
  showIncidentModal.value = false
}

const submitNearMiss = () => {
  console.log('Submitting near miss:', nearMissForm)
  // TODO: Implement near miss submission
  showNearMissModal.value = false
}

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

// Methods
const loadSafetyData = async () => {
  try {
    isLoading.value = true

    if (isAdminRoute.value) {
      await loadAdminSafetyData()
      return
    }
    
    // Load safety statistics
    const statsResponse = await fetch('/api/user/safety/stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (statsResponse.ok) {
      const statsData = await statsResponse.json()
      Object.assign(safetyStats.value, statsData)
    }
    
    // Load user certifications
    const certsResponse = await fetch('/api/user/safety/certifications', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (certsResponse.ok) {
      const certsData = await certsResponse.json()
      userCertifications.value = certsData.certifications || []
    }
    
    if (isAdmin.value) {
      await loadAdminSafetyData()
    }
    
  } catch (error) {
    console.error('Error loading safety data:', error)
  } finally {
    isLoading.value = false
  }
}

const loadAdminSafetyData = async () => {
  try {
    // Load all incidents
    const adminToken = localStorage.getItem('adminToken') || userStore.authToken || ''
    const incidentsResponse = await fetch('/api/admin/safety/incidents', {
      headers: {
        'Authorization': `Bearer ${adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (incidentsResponse.ok) {
      const incidentsData = await incidentsResponse.json()
      allIncidents.value = incidentsData.incidents || []
      incidents.value = incidentsData.incidents || []
    }
    
    // Load certifications
    const certsResponse = await fetch('/api/admin/safety/certifications', {
      headers: {
        'Authorization': `Bearer ${adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (certsResponse.ok) {
      const certsData = await certsResponse.json()
      certifications.value = certsData.certifications || []
    }
    
  } catch (error) {
    console.error('Error loading admin safety data:', error)
  }
}

// Lifecycle
// User-specific methods
const submitUserIncident = async () => {
  try {
    isSubmitting.value = true
    const response = await fetch('/api/user/safety/incidents', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userIncidentForm)
    })
    
    if (response.ok) {
      showUserIncidentModal.value = false
      // Reset form
      userIncidentForm.type = ''
      userIncidentForm.description = ''
      userIncidentForm.location = ''
      // Reload data
      await loadSafetyData()
    }
  } catch (error) {
    console.error('Error submitting incident:', error)
  } finally {
    isSubmitting.value = false
  }
}

onMounted(async () => {
  // Initialize safety data with real API calls
  await loadSafetyData()
  console.log('Safety Compliance Hub loaded with real data')
})
</script>

<style scoped>
/* Background patterns and animations */
.bg-grid-pattern {
  background-image: radial-gradient(circle, rgba(148, 163, 184, 0.3) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: grid-move 20s linear infinite;
}

@keyframes grid-move {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

/* Floating particles */
.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: linear-gradient(45deg, #10b981, #06b6d4);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.particle-1 { top: 20%; left: 10%; animation-delay: 0s; }
.particle-2 { top: 60%; left: 20%; animation-delay: 1s; }
.particle-3 { top: 40%; left: 80%; animation-delay: 2s; }
.particle-4 { top: 80%; left: 70%; animation-delay: 3s; }
.particle-5 { top: 30%; left: 60%; animation-delay: 4s; }

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
  50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
}

/* Safety Components */
.safety-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

.stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

.stat-content {
  @apply flex-1;
}

.stat-label {
  @apply text-sm font-medium text-slate-400 mb-2;
}

.stat-value {
  @apply text-3xl font-bold text-white mb-2;
}

.stat-change {
  @apply text-sm font-medium;
}

.stat-item {
  @apply flex items-start space-x-4;
}

.stat-icon {
  @apply w-12 h-12 rounded-xl flex items-center justify-center;
}

/* Form styles */
.form-group {
  @apply space-y-2;
}

.form-label {
  @apply block text-sm font-medium text-slate-300;
}

.form-input {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-300;
}

.form-select {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-300;
}

.form-select option {
  @apply bg-slate-700 text-white;
}

/* Button styles */
.btn-oilbuddy-primary {
  @apply bg-gradient-to-r from-emerald-400 to-cyan-400 hover:from-emerald-500 hover:to-cyan-500 text-slate-900 font-medium px-4 py-2 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl;
}

/* User dashboard styles */
.dashboard-stat-card {
  @apply bg-slate-800/90 backdrop-blur-xl rounded-3xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

/* Sidebar navigation styles - matching admin dashboard */
.nav-item {
  @apply flex items-center space-x-3 px-4 py-3 text-slate-300 rounded-xl hover:bg-slate-800/50 hover:text-emerald-400 transition-all duration-300;
}

.nav-item.router-link-active {
  @apply bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 text-emerald-400 shadow-lg shadow-emerald-500/10;
}

/* Custom scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.5) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.5);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.8);
}

.dashboard-action-card {
  @apply bg-slate-800/90 backdrop-blur-xl rounded-3xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300 cursor-pointer;
}

.dashboard-card {
  @apply bg-slate-800/90 backdrop-blur-xl rounded-3xl p-6 border border-slate-700/50;
}

/* Badge styles */
.badge-success {
  @apply bg-emerald-500/20 text-emerald-400 border border-emerald-500/30;
}

.badge-warning {
  @apply bg-yellow-500/20 text-yellow-400 border border-yellow-500/30;
}

.badge-danger {
  @apply bg-red-500/20 text-red-400 border border-red-500/30;
}

.badge-oil {
  @apply bg-slate-500/20 text-slate-400 border border-slate-500/30;
}

.badge-primary {
  @apply bg-blue-500/20 text-blue-400 border border-blue-500/30;
}
</style>
